import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import useQuery from "../../hooks/useQuery";

import useStyles from "./filter.styles";

import {
  selectCurrentFilterWithCount,
  selectIsCollectionItemsFetching,
  selectIsFilterWithCountFetching,
} from "../../redux/shop/shop.selectors";

import Loading from "../loading";
import { useMediaQuery } from "@material-ui/core";
import consoleLog from "../../utils/consoleLog";

interface FilterElementType {
  filterType: string;
  filterId: string;
  filterName: string;
  filterLabel: string;
  filterValue: string;
  numberOfResult?: number;
}

interface FilterPriceElementType {
  filterType: string;
  filterId: string;
  filterName: string;
  filterLabel: string;
  filterValue: string;
  min?: number;
  max?: number;
  numberOfResult?: number;
}

const FilterElement = (props: any) => {
  const {
    filterType,
    filterId,
    filterLabel,
    filterValue,
    numberOfResult,
    handleChange,
    handleClear,
    terms,
  } = props;

  const matches = useMediaQuery("(min-width:1200px)");

  const [checked, setChecked] = useState(
    filterValue && filterId === "PRICE"
      ? terms?.[filterId]?.selectedOptions.some(
          (term: any) => term.title === JSON.parse(filterValue).title,
        )
      : terms?.[filterId]?.selectedOptions.some(
          (term: string) => term === filterValue,
        ),
  );

  useEffect(() => {
    setChecked(
      filterValue && filterId === "PRICE"
        ? terms?.[filterId]?.selectedOptions.some(
            (term: any) => term.title === JSON.parse(filterValue).title,
          )
        : terms?.[filterId]?.selectedOptions.some(
            (term: string) => term === filterValue,
          ),
    );
  }, [terms, handleChange, handleClear, filterId, filterValue]);

  const classes = useStyles({ ...props, matches, checked });

  return (
    <div className={classes.element}>
      <label className={classes.elementLabel}>
        <input
          type="checkbox"
          name={`${filterType}#${filterId}`}
          value={filterValue}
          onChange={handleChange}
          checked={checked}
        />
        <div></div>
        {filterLabel.toLowerCase()}
      </label>
      <span>({numberOfResult})</span>
    </div>
  );
};

const SubFilter = (props: any) => {
  const location = useLocation();
  const matches = useMediaQuery("(min-width:1200px)");

  const { elements, terms, handleChange, handleClear, type } = props;

  const [numberOfElements, setNumberOfElements] = useState(elements?.length);
  const [semiExpanded, setSemiExpanded] = useState<boolean>(true);
  const [fullExpanded, setFullExpanded] = useState<boolean>(false);

  const sortedElements =
    type === "PRICE" || type === "RATING"
      ? elements
      : elements?.sort((a: any, b: any) => b.numberOfResult - a.numberOfResult);

  const classes = useStyles({ ...props, matches });

  const toggleExpanded = () => {
    setSemiExpanded(!semiExpanded);
  };

  const toggleFullExpanded = () => {
    setFullExpanded(!fullExpanded);
  };

  useEffect(() => {
    setNumberOfElements(elements.length);
  }, [elements]);

  useEffect(() => {
    setSemiExpanded(true);
    setFullExpanded(false);
  }, [location]);

  return (
    <div className={classes.subFilterContainer}>
      <div onClick={toggleExpanded} className={classes.subFilterTitleAndExpand}>
        <div className={classes.subFilterTitle}>
          {elements[0].filterName.replaceAll("_", " ").toUpperCase()}
          {!semiExpanded &&
          terms?.[elements[0].filterId]?.selectedOptions &&
          terms[elements[0].filterId].selectedOptions.length !== 0 ? (
            <div className={classes.dot}></div>
          ) : null}
        </div>
        <div className={classes.expanded}>
          {semiExpanded ? (
            <svg
              width="24"
              height="24"
              xmlns="http://www.w3.org/2000/svg"
              fillRule="evenodd"
              clipRule="evenodd"
              viewBox="-12 -12 48 48"
            >
              <path d="M0 12v1h23v-1h-23z" />
            </svg>
          ) : (
            <svg
              width="24"
              height="24"
              xmlns="http://www.w3.org/2000/svg"
              fillRule="evenodd"
              clipRule="evenodd"
              viewBox="-12 -12 48 48"
            >
              <path d="M23.245 4l-11.245 14.374-11.219-14.374-.781.619 12 15.381 12-15.391-.755-.609z" />
            </svg>
          )}
        </div>
      </div>
      {semiExpanded ? (
        <div>
          <div className={classes.filterElementContainer}>
            {numberOfElements > 6 && fullExpanded
              ? sortedElements.map(
                  (
                    {
                      filterType,
                      filterId,
                      filterLabel,
                      filterValue,
                      numberOfResult,
                    }: FilterElementType,
                    index: number,
                  ) => (
                    <FilterElement
                      key={index}
                      filterType={filterType}
                      filterId={filterId}
                      filterLabel={filterLabel}
                      filterValue={filterValue}
                      numberOfResult={numberOfResult}
                      handleChange={handleChange}
                      handleClear={handleClear}
                      terms={terms}
                    />
                  ),
                )
              : sortedElements
                  .filter((_: any, index: number) => index < 6)
                  .map(
                    (
                      {
                        filterType,
                        filterId,
                        filterLabel,
                        filterValue,
                        numberOfResult,
                      }: FilterElementType,
                      index: number,
                    ) => (
                      <FilterElement
                        key={index}
                        filterType={filterType}
                        filterId={filterId}
                        filterLabel={filterLabel}
                        filterValue={filterValue}
                        numberOfResult={numberOfResult}
                        handleChange={handleChange}
                        handleClear={handleClear}
                        terms={terms}
                      />
                    ),
                  )}
          </div>
          <div className={classes.showMore}>
            {numberOfElements > 6 ? (
              fullExpanded ? (
                <span onClick={toggleFullExpanded}>SHOW LESS</span>
              ) : (
                <span onClick={toggleFullExpanded}>
                  {numberOfElements - 6} MORE
                </span>
              )
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
  // : null;
};

const Filter = (props: any) => {
  const dispatch = useDispatch();
  const query = useQuery();
  const matches = useMediaQuery("(min-width:1200px)");

  const {
    open,
    sortByQuery,
    filterByQuery,
    setFilterByQuery,
    filterTerms,
    setFilterTerms,
    extraFilterTerms,
    setExtraFilterTerms,
    fetchCollectionItems,
    fetchBrandItems,
    currentBrand,
  } = props;

  const currentFilter = useSelector(selectCurrentFilterWithCount);

  const isLoading = useSelector(selectIsFilterWithCountFetching);
  const isProcessing = useSelector(selectIsCollectionItemsFetching);
  const [isClearing, setIsClearing] = useState(false);

  const [currentCollectionId] = useState(
    query.has("collectionId") ? query.get("collectionId") : "",
  );

  const [filters, setFilters] = useState(currentFilter?.FILTERS);

  const [extraFilters, setExtraFilters] = useState(currentFilter?.EXTRAFILTERS);

  useEffect(() => {
    setFilters(currentFilter?.FILTERS);
  }, [currentFilter]);

  useEffect(() => {
    setExtraFilters(currentFilter?.EXTRAFILTERS);
  }, [currentFilter]);

  const classes = useStyles({ open, matches });

  // set elements for filter (pet, categories, brand, price, customer rating, ...)
  let filterElements: {
    [name: string]: FilterElementType[] | FilterPriceElementType[];
  } = {};

  if (filters) {
    Object.keys(filters).forEach((filterKey: any) => {
      if (Object.keys(filters[filterKey].result).length !== 0) {
        filterElements[filterKey] = Object.keys(filters[filterKey].result).map(
          (filterLabel: string) => ({
            filterType: "FILTER",
            filterId: filterKey.includes("FIL_")
              ? filterKey
              : // .toUpperCase().replaceAll(" ", "_")
                `FIL_${filterKey}`,
            filterName: filters[filterKey].label.toUpperCase(),
            filterLabel: filterLabel.toUpperCase(),
            filterValue: filterLabel,
            numberOfResult: filters[filterKey].result[filterLabel],
          }),
        );
      }
    });
  }

  if (extraFilters) {
    Object.keys(extraFilters).forEach((extraFilterKey: any) => {
      if (
        extraFilters?.[extraFilterKey]?.result &&
        Object.keys(extraFilters?.[extraFilterKey]?.result).length !== 0
      ) {
        if (extraFilterKey === "CATEGORIES_BY_CHILDREN") {
          filterElements[extraFilterKey] = Object.keys(
            extraFilters?.[extraFilterKey]?.result,
          ).map((filterLabel: any) => ({
            filterType: "EXTRAFILTER",
            filterId: extraFilterKey,
            filterName: extraFilters[extraFilterKey].label.toUpperCase(),
            filterLabel:
              extraFilters?.[extraFilterKey]?.result[filterLabel]["label"],
            filterValue: filterLabel,
            numberOfResult:
              extraFilters?.[extraFilterKey]?.result[filterLabel]["counter"],
          }));
        } else if (extraFilterKey === "CATEGORIES_BY_PRODUCT") {
          filterElements[extraFilterKey] = Object.keys(
            extraFilters?.[extraFilterKey]?.result,
          ).map((filterLabel: any) => ({
            filterType: "EXTRAFILTER",
            filterId: extraFilterKey,
            filterName: extraFilters[extraFilterKey].label.toUpperCase(),
            filterLabel:
              extraFilters?.[extraFilterKey]?.result[filterLabel]["label"],
            filterValue: filterLabel,
            numberOfResult:
              extraFilters?.[extraFilterKey]?.result[filterLabel]["counter"],
          }));
        } else if (extraFilterKey === "PRICE") {
          if (
            !Object.keys(extraFilters?.[extraFilterKey]?.result).some(
              (result) => result === "undefined",
            )
          ) {
            filterElements[extraFilterKey] = Object.keys(
              extraFilters?.[extraFilterKey]?.result,
            ).map((filterLabel: any) => ({
              filterType: "EXTRAFILTER",
              filterId: extraFilterKey,
              filterName: extraFilters[extraFilterKey].label.toUpperCase(),
              filterLabel: filterLabel.toUpperCase(),
              filterValue: JSON.stringify(
                extraFilters[extraFilterKey].options.find(
                  (option: any) => option.title === filterLabel,
                ),
              ),
              numberOfResult:
                extraFilters?.[extraFilterKey]?.result[filterLabel],
            }));
          }
        } else {
          filterElements[extraFilterKey] = Object.keys(
            extraFilters?.[extraFilterKey]?.result,
          ).map((filterLabel: any) => ({
            filterType: "EXTRAFILTER",
            filterId: extraFilterKey,
            filterName: extraFilters[extraFilterKey].label.toUpperCase(),
            filterLabel: filterLabel.toUpperCase(),
            filterValue: filterLabel,
            numberOfResult: extraFilters?.[extraFilterKey]?.result[filterLabel],
          }));
        }
      }
    });
  }

  // handle filter term change
  const handleTermChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;
    const [filterType, filterId] = name.split("#");

    const tempFilterTerms = Object.assign({}, filterTerms);
    const tempExtraFilterTerms = Object.assign({}, extraFilterTerms);

    if (filterType === "FILTER") {
      if (
        tempFilterTerms[filterId]?.selectedOptions?.some(
          (option: any) => option === value,
        )
      ) {
        setFilterTerms({
          ...tempFilterTerms,
          [filterId]: {
            selectedOptions: tempFilterTerms[filterId].selectedOptions.filter(
              (option: string) => option !== value,
            ),
          },
        });
      } else {
        if (!tempFilterTerms[filterId]?.selectedOptions) {
          setFilterTerms({
            ...tempFilterTerms,
            [filterId]: { selectedOptions: [value] },
          });
        } else {
          setFilterTerms({
            ...tempFilterTerms,
            [filterId]: {
              selectedOptions: [
                ...tempFilterTerms[filterId].selectedOptions,
                value,
              ],
            },
          });
        }
      }
    } else {
      switch (filterId) {
        case "PRICE":
          if (
            extraFilterTerms[filterId]?.selectedOptions?.some(
              (option: any) => option.title === JSON.parse(value).title,
            )
          ) {
            setExtraFilterTerms({
              ...tempExtraFilterTerms,
              [filterId]: {
                selectedOptions: tempExtraFilterTerms[
                  filterId
                ].selectedOptions.filter(
                  (option: any) => option.title !== JSON.parse(value).title,
                ),
              },
            });
          } else {
            if (!tempExtraFilterTerms[filterId]?.selectedOptions) {
              setExtraFilterTerms({
                ...tempExtraFilterTerms,
                [filterId]: { selectedOptions: [JSON.parse(value)] },
              });
            } else {
              setExtraFilterTerms({
                ...tempExtraFilterTerms,
                [filterId]: {
                  selectedOptions: [
                    ...tempExtraFilterTerms[filterId].selectedOptions,
                    JSON.parse(value),
                  ],
                },
              });
            }
          }
          break;
        default:
          if (
            extraFilterTerms[filterId]?.selectedOptions?.some(
              (option: any) => option === value,
            )
          ) {
            setExtraFilterTerms({
              ...tempExtraFilterTerms,
              [filterId]: {
                selectedOptions: tempExtraFilterTerms[
                  filterId
                ].selectedOptions.filter((option: string) => option !== value),
              },
            });
          } else {
            if (!tempExtraFilterTerms[filterId]?.selectedOptions) {
              setExtraFilterTerms({
                ...tempExtraFilterTerms,
                [filterId]: { selectedOptions: [value] },
              });
            } else {
              setExtraFilterTerms({
                ...tempExtraFilterTerms,
                [filterId]: {
                  selectedOptions: [
                    ...tempExtraFilterTerms[filterId].selectedOptions,
                    value,
                  ],
                },
              });
            }
          }
          break;
      }
    }
  };

  useEffect(() => {
    consoleLog("filterTerms", filterTerms);
  }, [filterTerms]);
  useEffect(() => {
    consoleLog("extraFilterTerms", extraFilterTerms);
  }, [extraFilterTerms]);

  // handle clear filter term
  const handleTermClear = () => {
    setIsClearing(true);
    setTimeout(() => {
      setFilterTerms({});
      setExtraFilterTerms({});
      setIsClearing(false);
    }, 300);
  };

  // handle submit to show filter result
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const tempFilterTerms = Object.assign({}, filterTerms);
    const tempExtraFilterTerms = Object.assign({}, extraFilterTerms);

    Object.keys(tempFilterTerms).forEach((filterKey) => {
      if (
        tempFilterTerms[filterKey] &&
        tempFilterTerms[filterKey].selectedOptions.length === 0
      ) {
        delete tempFilterTerms[filterKey];
      }
    });

    Object.keys(tempExtraFilterTerms).forEach((filterKey) => {
      if (
        tempExtraFilterTerms[filterKey] &&
        tempExtraFilterTerms[filterKey].selectedOptions.length === 0
      ) {
        delete tempExtraFilterTerms[filterKey];
      }
    });

    setFilterTerms(tempFilterTerms);
    setExtraFilterTerms(tempExtraFilterTerms);

    if (query.has("collectionId")) {
      fetchCollectionItems(dispatch, {
        currentCollectionId,
        filterTerms: tempFilterTerms,
        extraFilterTerms: tempExtraFilterTerms,
        sortBy: sortByQuery ? sortByQuery : undefined,
      });
    } else if (query.has("brandId")) {
      fetchBrandItems(dispatch, {
        searchKeyword: currentBrand?.brandName,
        filterTerms: tempFilterTerms,
        extraFilterTerms: tempExtraFilterTerms,
        sortBy: sortByQuery ? sortByQuery : undefined,
      });
    }
  };

  useEffect(() => {
    let tempFilterTemrs = {};
    let tempExtraFilterTerms = {};

    consoleLog("filterByQuery", filterByQuery);

    if (filterByQuery) {
      let splittedFilterQuery = filterByQuery.split("+");

      splittedFilterQuery.forEach((filterTerm: any) => {
        let splittedFilterTerm = filterTerm.split("#");

        let filterId = splittedFilterTerm[0];
        // .toUpperCase().replaceAll(" ", "_");

        let shiftedFilterTerms = Object.assign([], splittedFilterTerm);
        shiftedFilterTerms.shift();

        if (filterId === "PRICE") {
          shiftedFilterTerms = shiftedFilterTerms.map((term: any) =>
            JSON.parse(term),
          );
        }

        if (filterId.length !== 0) {
          if (filterId.includes("FIL_")) {
            tempFilterTemrs = {
              ...tempFilterTemrs,
              [filterId]: {
                selectedOptions: shiftedFilterTerms,
              },
            };
          } else {
            tempExtraFilterTerms = {
              ...tempExtraFilterTerms,
              [filterId]: {
                selectedOptions: shiftedFilterTerms,
              },
            };
          }
        }
      });
    }

    setFilterTerms(tempFilterTemrs);
    setExtraFilterTerms(tempExtraFilterTerms);
  }, [location, currentFilter]);

  useEffect(() => {
    let filterByQueryArr: string[] = [];

    Object.keys(filterTerms).forEach((filterId) => {
      let termArr = [filterId, ...filterTerms[filterId].selectedOptions];

      let joinedTermArr = termArr.join("#");

      filterByQueryArr.push(joinedTermArr);
    });

    Object.keys(extraFilterTerms).forEach((filterId) => {
      let selectedOptions =
        filterId === "PRICE"
          ? extraFilterTerms[filterId].selectedOptions.map((option: any) =>
              JSON.stringify(option),
            )
          : extraFilterTerms[filterId].selectedOptions;

      let termArr = [filterId, ...selectedOptions];

      let joinedTermArr = termArr.join("#");

      filterByQueryArr.push(joinedTermArr);
    });

    setFilterByQuery(filterByQueryArr.join("+"));
  }, [filterTerms, extraFilterTerms]);

  useEffect(() => {
    if (filterByQuery) {
      query.set("filterBy", filterByQuery);
    } else if (filterByQuery === "") {
      query.delete("filterBy");
    }

    if (sortByQuery) {
      query.set("sortBy", sortByQuery);
    } else if (sortByQuery === "") {
      query.delete("sortBy");
    }

    history.replaceState({}, "", `${location.pathname}?${query.toString()}`);
  }, [filterByQuery, sortByQuery]);

  return (
    <form className={classes.container} onSubmit={handleSubmit}>
      <div className={classes.btnContainer}>
        <button
          type="button"
          onClick={handleTermClear}
          className={classes.clearBtn}
        >
          {isClearing ? (
            <Loading on size="1rem" marginLeft="0.7rem" />
          ) : (
            "Clear"
          )}
        </button>
        <button type="submit" className={classes.showResult}>
          {isProcessing ? (
            <Loading on size="1.5rem" color="white" />
          ) : (
            `Show results`
          )}
        </button>
      </div>
      <div className={classes.filterContainer}>
        {isLoading ? (
          <Loading on marginLeft="auto" marginRight="auto" />
        ) : (
          Object.keys(filterElements)
            .sort((a: string, b: string) =>
              filterElements[a][0].filterName > filterElements[b][0].filterName
                ? 1
                : -1,
            )
            .map((key, index) => (
              <SubFilter
                key={index}
                type={key}
                elements={filterElements[key]}
                terms={
                  filterElements[key].some((el) => el.filterType === "FILTER")
                    ? filterTerms
                    : extraFilterTerms
                }
                handleChange={handleTermChange}
                handleClear={handleTermClear}
              />
            ))
        )}
      </div>
    </form>
  );
};

export default Filter;
